<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <login-layout>
    <div :class="['aqe-login-agent', `--${agentState}`]" aria-live="polite">
      <span class="aqe-login-orb" aria-hidden="true"></span>
      <span class="aqe-login-agent__text">{{ agentLine }}</span>
    </div>
    <oxd-text class="orangehrm-login-title" tag="h5">
      {{ $t('auth.login') }}
    </oxd-text>
    <p class="aqe-login-sub" :aria-label="welcomeText">
      <span aria-hidden="true">{{ welcomeTyped }}</span>
      <span
        v-if="welcomeTyped.length < welcomeText.length"
        class="aqe-login-sub__caret"
        aria-hidden="true"
      ></span>
    </p>
    <div
      ref="formRoot"
      class="orangehrm-login-form"
      @focusin="onFieldFocus"
      @focusout="onFieldBlur"
    >
      <div class="orangehrm-login-error">
        <oxd-alert
          :show="error !== null"
          :message="error?.message || ''"
          type="error"
        ></oxd-alert>
        <oxd-sheet
          v-if="isDemoMode"
          type="gray-lighten-2"
          class="orangehrm-demo-credentials"
        >
          <oxd-text tag="p">Username : Admin</oxd-text>
          <oxd-text tag="p">Password : admin123</oxd-text>
        </oxd-sheet>
      </div>
      <oxd-form
        ref="loginForm"
        method="post"
        :action="submitUrl"
        @submit-valid="onSubmit"
      >
        <input name="_token" :value="token" type="hidden" />
        <input name="aqe_g" :value="seal.g" type="hidden" />
        <input name="aqe_c" :value="seal.c" type="hidden" />

        <oxd-form-row>
          <oxd-input-field
            v-model="username"
            name="username"
            :label="$t('general.username')"
            label-icon="person"
            :placeholder="$t('auth.username')"
            :rules="rules.username"
            autofocus
          />
        </oxd-form-row>

        <oxd-form-row>
          <div
            class="aqe-login-password"
            @keydown="checkCapsLock"
            @keyup="checkCapsLock"
          >
            <oxd-input-field
              v-model="password"
              name="password"
              :label="$t('general.password')"
              label-icon="key"
              :placeholder="$t('auth.password')"
              :type="showPassword ? 'input' : 'password'"
              :rules="rules.password"
            />
            <button
              type="button"
              class="aqe-login-password__toggle"
              :aria-label="showPassword ? 'Hide password' : 'Show password'"
              :aria-pressed="showPassword ? 'true' : 'false'"
              :title="showPassword ? 'Hide password' : 'Show password'"
              @click="showPassword = !showPassword"
            >
              <oxd-icon :name="showPassword ? 'eye-slash' : 'eye'" />
            </button>
            <p v-if="capsLock" class="aqe-login-password__caps" role="status">
              <oxd-icon name="capslock-fill" />
              Caps Lock is on
            </p>
          </div>
        </oxd-form-row>

        <oxd-form-actions class="orangehrm-login-action">
          <oxd-button
            :class="['orangehrm-login-button', {'aqe-is-loading': submitted}]"
            display-type="main"
            :label="$t('auth.login')"
            type="submit"
          />
        </oxd-form-actions>
        <ol v-if="submitted" class="aqe-login-steps" aria-live="polite">
          <li
            v-for="(step, i) in steps"
            :key="step"
            :class="{
              '--done': i < stepIndex,
              '--active': i === stepIndex,
            }"
          >
            <span class="aqe-login-steps__dot" aria-hidden="true"></span>
            {{ step }}
          </li>
        </ol>
        <div v-else class="orangehrm-login-forgot">
          <oxd-text class="orangehrm-login-forgot-header" @click="navigateUrl">
            {{ $t('auth.forgot_password') }}?
          </oxd-text>
        </div>
      </oxd-form>
      <template v-if="authenticators.length > 0">
        <oxd-divider class="orangehrm-login-seperator"></oxd-divider>
        <social-media-auth :authenticators="authenticators"></social-media-auth>
      </template>
    </div>
    <div class="orangehrm-login-footer">
      <div v-if="showSocialMedia" class="orangehrm-login-footer-sm">
        <a
          href="https://www.linkedin.com/company/orangehrm/mycompany/"
          target="_blank"
        >
          <oxd-icon type="svg" class="orangehrm-sm-icon" name="linkedinFill" />
        </a>
        <a href="https://www.facebook.com/OrangeHRM/" target="_blank">
          <oxd-icon type="svg" class="orangehrm-sm-icon" name="facebookFill" />
        </a>
        <a href="https://twitter.com/orangehrm?lang=en" target="_blank">
          <oxd-icon type="svg" class="orangehrm-sm-icon" name="twitterFill" />
        </a>
        <a href="https://www.youtube.com/c/OrangeHRMInc" target="_blank">
          <oxd-icon type="svg" class="orangehrm-sm-icon" name="youtubeFill" />
        </a>
      </div>
      <slot name="footer"></slot>
    </div>
  </login-layout>
</template>

<script>
import {urlFor} from '@ohrm/core/util/helper/url';
import {OxdAlert, OxdIcon, OxdSheet} from '@ohrm/oxd';
import {required} from '@ohrm/core/util/validation/rules';
import {navigate, reloadPage} from '@ohrm/core/util/helper/navigation';
import LoginLayout from '@/orangehrmAuthenticationPlugin/components/LoginLayout.vue';
import SocialMediaAuth from '@/orangehrmAuthenticationPlugin/components/SocialMediaAuth.vue';
import {createLoginGuard} from '@/orangehrmAuthenticationPlugin/util/loginGuard';

export default {
  components: {
    'oxd-icon': OxdIcon,
    'oxd-alert': OxdAlert,
    'oxd-sheet': OxdSheet,
    'login-layout': LoginLayout,
    'social-media-auth': SocialMediaAuth,
  },

  props: {
    error: {
      type: Object,
      default: () => null,
    },
    token: {
      type: String,
      required: true,
    },
    showSocialMedia: {
      type: Boolean,
      default: true,
    },
    isDemoMode: {
      type: Boolean,
      default: false,
    },
    authenticators: {
      type: Array,
      default: () => [],
    },
    guard: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      username: '',
      password: '',
      rules: {
        username: [required],
        password: [required],
      },
      submitted: false,
      showPassword: false,
      capsLock: false,
      seal: {g: '', c: ''},
      focused: false,
      welcomeText: "Welcome back. Sign in and I'll pick up where you left off.",
      welcomeTyped: '',
      steps: [
        "Verifying it's really you",
        'Securing your session',
        'Opening your workspace',
      ],
      stepIndex: 0,
      agentTimers: [],
    };
  },

  computed: {
    submitUrl() {
      return urlFor('/auth/validate');
    },
    agentState() {
      if (this.submitted) return 'thinking';
      if (this.focused) return 'listening';
      if (this.error) return 'alert';
      return 'idle';
    },
    agentLine() {
      if (this.submitted) return 'Thinking\u2026';
      if (this.focused) return 'Listening\u2026';
      if (this.error) return "That didn't work. Let's try again.";
      const h = new Date().getHours();
      if (h < 5) return "Working late? I'm here.";
      if (h < 12) return 'Good morning';
      if (h < 17) return 'Good afternoon';
      return 'Good evening';
    },
  },

  mounted() {
    this.loginGuard = createLoginGuard(this.$refs.formRoot);
    this.typeWelcome();
  },

  beforeUnmount() {
    if (this.loginGuard) this.loginGuard.destroy();
    this.agentTimers.forEach((t) => clearTimeout(t));
  },

  beforeMount() {
    setTimeout(() => {
      reloadPage();
    }, 1200000); // 20 * 60 * 1000 (20 minutes);
  },

  methods: {
    later(fn, ms) {
      this.agentTimers.push(setTimeout(fn, ms));
    },
    typeWelcome() {
      const reduce =
        window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduce) {
        this.welcomeTyped = this.welcomeText;
        return;
      }
      const step = () => {
        const n = this.welcomeTyped.length + 1;
        this.welcomeTyped = this.welcomeText.slice(0, n);
        if (n < this.welcomeText.length)
          this.later(step, 22 + Math.random() * 30);
      };
      this.later(step, 450);
    },
    onFieldFocus(event) {
      if (event.target && event.target.tagName === 'INPUT') this.focused = true;
    },
    onFieldBlur() {
      this.focused = false;
    },
    runSteps() {
      this.stepIndex = 0;
      const tick = () => {
        if (this.stepIndex < this.steps.length - 1) {
          this.stepIndex += 1;
          this.later(tick, 750);
        }
      };
      this.later(tick, 700);
    },
    async onSubmit() {
      if (this.submitted) return;
      this.submitted = true;
      this.runSteps();
      try {
        this.seal = await this.loginGuard.seal(this.guard);
      } catch (e) {
        this.seal = {g: '', c: ''};
      }
      await this.$nextTick();
      this.$refs.loginForm.$el.submit();
    },
    checkCapsLock(event) {
      if (event && typeof event.getModifierState === 'function') {
        this.capsLock = event.getModifierState('CapsLock');
      }
    },
    navigateUrl() {
      navigate('/auth/requestPasswordResetCode');
    },
  },
};
</script>

<style src="./login.scss" lang="scss" scoped></style>
